import React, { useMemo, useState } from 'react';
import { useAppSelector } from 'hooks';

import { ClickableIcon } from '~components';
import { Select } from '~components/form-fields/inputs';
import { faPlus } from '~components/icon';
import { TableView } from '~components/table-view';

import cutoverPairColumnsFactory from '~support-at-home/forms/sah-associations/cutoverPairs';

import { getAll as getContributionCategories } from '~lookups/actions/sahCategories';
import {
  CutoverPair,
  CutoverServiceOption,
  useGetCutoverServicesQuery,
} from '~support-at-home/actions/sahServiceCutover';

import { selectLookupSahCategories } from '~lookups/selectors/sahCategories';

interface ComponentProps {
  value: { cutover_date?: string; pairs?: CutoverPair[] };
  onChange?: (name: string, value: any) => any;
}

/**
 * Step 1 - the replacements a run is made of, built one at a time.
 *
 * Each row of the builder narrows left to right: category, then service type, then service. The
 * service type is the grouping a provider names a change by - "the personal care services" - and is
 * what turns a catalogue of dozens into a handful. It is optional, and appears on every service
 * option regardless, so the list stays readable whether or not it is used.
 *
 * The two sides filter on different things, and deliberately:
 *
 *  - the service being replaced is narrowed to ones this company has an association for, since a
 *    service nothing is mapped to has nothing hanging off it to carry;
 *  - the replacement is narrowed to ones available on the cutover date, which is the only thing that
 *    has to be true of it. Nothing requires it to be another period of the same service - a provider
 *    may be replacing a service with a genuinely different one, and this is where that is allowed.
 *
 * Add appends to the list below, so one run can cover several replacements.
 */
const SelectServicePairs: React.FC<ComponentProps> = ({ value, onChange }) => {
  const { cutover_date, pairs = [] } = value;
  const categories = useAppSelector(selectLookupSahCategories);

  const [fromCategoryId, setFromCategoryId] = useState<string | null>(null);
  const [fromSahTypeId, setFromSahTypeId] = useState<string | null>(null);
  const [fromServiceId, setFromServiceId] = useState<string | null>(null);
  const [toCategoryId, setToCategoryId] = useState<string | null>(null);
  const [toSahTypeId, setToSahTypeId] = useState<string | null>(null);
  const [toServiceId, setToServiceId] = useState<string | null>(null);

  const { data: fromData } = useGetCutoverServicesQuery(
    { category_id: fromCategoryId as string, associated_only: true },
    { skip: !fromCategoryId },
  );
  const { data: toData } = useGetCutoverServicesQuery(
    { category_id: toCategoryId as string, effective_on: cutover_date },
    { skip: !toCategoryId || !cutover_date },
  );

  const categoryOptions = useMemo(() => categories.map(({ id, name }) => ({ id, name })), [categories]);

  // Derived from the services already fetched rather than looked up separately: these can only ever
  // be the service types present in this category, and deriving them keeps the two in step with no
  // second request to fall out of date.
  const sahTypeOptions = (rows: CutoverServiceOption[] | undefined) => {
    const seen = new Map<string, string>();
    for (const { sah_service_type_id, sah_service_type_name } of rows ?? []) {
      if (sah_service_type_id && sah_service_type_name && !seen.has(sah_service_type_id)) {
        seen.set(sah_service_type_id, sah_service_type_name);
      }
    }
    return [...seen.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((left, right) => left.name.localeCompare(right.name));
  };

  // Shown on every option, not only used as a filter. A provider who has not narrowed by service
  // type still needs to know that "Assistance with self-care and activities of daily living" is one
  // of the personal care services - which is the whole reason it is here.
  const serviceOptions = (rows: CutoverServiceOption[] | undefined, sahTypeId: string | null) =>
    (rows ?? [])
      .filter(({ sah_service_type_id }) => !sahTypeId || sah_service_type_id === sahTypeId)
      .map(({ service_id, name, sah_service_type_name }) => ({
        id: service_id,
        name: sah_service_type_name ? `${name} (${sah_service_type_name})` : name,
      }));

  // Already-added services drop out of the From list: one service cannot give way to two
  // replacements in the same run, and the server rejects it - better not to offer it.
  const added = new Set(pairs.map(({ from_service_id }) => from_service_id));
  const fromRows = (fromData?.rows ?? []).filter(({ service_id }) => !added.has(service_id));
  const fromOptions = serviceOptions(fromRows, fromSahTypeId);
  const toOptions = serviceOptions(toData?.rows, toSahTypeId);

  const categoryName = (id: string | null) => categories.find(({ id: categoryId }) => categoryId === id)?.name ?? '';

  // Name plus category. The two sides of a pair very often share a name - they are two periods of
  // the same government service, which is the ordinary case - so the name alone would give the table
  // two identical columns and no way to read what was actually added.
  const labelOf = (rows: CutoverServiceOption[] | undefined, id: string | null, category: string | null) => {
    const name = rows?.find(({ service_id }) => service_id === id)?.name ?? '';
    const label = categoryName(category);
    return label ? `${name} (${label})` : name;
  };

  const canAdd = Boolean(fromServiceId && toServiceId && fromServiceId !== toServiceId);

  const handleAdd = () => {
    if (!canAdd) return;

    onChange?.('pairs', [
      ...pairs,
      {
        from_service_id: fromServiceId,
        to_service_id: toServiceId,
        // Carried alongside the ids purely so the table below can name what was added without
        // re-fetching a category the pickers have since moved off. Stripped before the request.
        from_service_name: labelOf(fromData?.rows, fromServiceId, fromCategoryId),
        to_service_name: labelOf(toData?.rows, toServiceId, toCategoryId),
      },
    ]);

    setFromServiceId(null);
    setToServiceId(null);
  };

  const handleRemove = (row: any) =>
    onChange?.(
      'pairs',
      pairs.filter(({ from_service_id }) => from_service_id !== row.from_service_id),
    );

  const rows = pairs.map((pair) => ({ ...pair, id: pair.from_service_id }));

  const columns = useMemo(() => cutoverPairColumnsFactory(), []);

  return (
    <div>
      <div className="sah-cutover-pair-row">
        <div className="sah-cutover-pair-label">From:</div>
        <div className="sah-cutover-pair-category">
          <Select
            type="select"
            id="cutover-from-category"
            name="cutover-from-category"
            value={fromCategoryId}
            options={categoryOptions}
            selectorActions={getContributionCategories}
            placeholder="Category"
            onChange={(_name: string, next: any) => {
              setFromCategoryId(next ?? null);
              setFromSahTypeId(null);
              setFromServiceId(null);
            }}
          />
        </div>
        <div className="sah-cutover-pair-sah-type">
          <Select
            type="select"
            id="cutover-from-sah-type"
            name="cutover-from-sah-type"
            value={fromSahTypeId}
            options={sahTypeOptions(fromData?.rows)}
            disabled={!fromCategoryId}
            placeholder="All service types"
            onChange={(_name: string, next: any) => {
              setFromSahTypeId(next ?? null);
              setFromServiceId(null);
            }}
          />
        </div>
        <div className="sah-cutover-pair-service">
          <Select
            type="select"
            id="cutover-from-service"
            name="cutover-from-service"
            value={fromServiceId}
            options={fromOptions}
            disabled={!fromCategoryId}
            placeholder={fromCategoryId ? 'Service being replaced' : 'Choose a category first'}
            onChange={(_name: string, next: any) => setFromServiceId(next ?? null)}
          />
        </div>
        <div className="sah-cutover-pair-action" />
      </div>

      <div className="sah-cutover-pair-row">
        <div className="sah-cutover-pair-label">To:</div>
        <div className="sah-cutover-pair-category">
          <Select
            type="select"
            id="cutover-to-category"
            name="cutover-to-category"
            value={toCategoryId}
            options={categoryOptions}
            selectorActions={getContributionCategories}
            placeholder="Category"
            onChange={(_name: string, next: any) => {
              setToCategoryId(next ?? null);
              setToSahTypeId(null);
              setToServiceId(null);
            }}
          />
        </div>
        <div className="sah-cutover-pair-sah-type">
          <Select
            type="select"
            id="cutover-to-sah-type"
            name="cutover-to-sah-type"
            value={toSahTypeId}
            options={sahTypeOptions(toData?.rows)}
            disabled={!toCategoryId || !cutover_date}
            placeholder="All service types"
            onChange={(_name: string, next: any) => {
              setToSahTypeId(next ?? null);
              setToServiceId(null);
            }}
          />
        </div>
        <div className="sah-cutover-pair-service">
          <Select
            type="select"
            id="cutover-to-service"
            name="cutover-to-service"
            value={toServiceId}
            options={toOptions}
            disabled={!toCategoryId || !cutover_date}
            placeholder={cutover_date ? 'Replacement service' : 'Choose a cutover date first'}
            onChange={(_name: string, next: any) => setToServiceId(next ?? null)}
          />
        </div>
        <div className="sah-cutover-pair-action">
          {/* Filled green the moment both sides are chosen. Nothing is added to the run until this
              is clicked, and a provider who fills the pickers and moves straight on adds nothing at
              all - so the control that completes the step says when it is ready to be used. */}
          <ClickableIcon
            id="cutover-add-pair"
            dataCy="cutover-add-pair"
            icon={faPlus}
            toolTip="Add this replacement"
            ariaLabel="Add this replacement"
            disabled={!canAdd}
            showTooltipWhenDisabled
            className={canAdd ? 'sah-cutover-add-available' : ''}
            color={canAdd ? null : 'black'}
            onClick={handleAdd}
          />
        </div>
      </div>

      {/* canDeleteRow/onDeleteRow are not used: TableView drops its entire actions cell under
          `readonly`, so both were inert and a mis-stated replacement could only be cleared by closing
          the assistant and starting again. The cross is a column instead - see cutoverPairs. */}
      <TableView
        id="cutover-pairs-table"
        data={rows}
        columns={columns}
        context={{ onRemovePair: handleRemove }}
        readonly
        striped
        showAddRow={false}
        emptyMessage="No replacements added yet - use the plus button above to add one."
      />

    </div>
  );
};

export default SelectServicePairs;
